/**
 * EarniWalletModal.jsx — Composant racine du modal de connexion/gestion wallet.
 *
 * Responsabilités :
 *   1. Affiche le shell DA (ModalShell + ModalHeader violet + croix cerclée §12 sexies)
 *   2. Instancie les hooks useWalletStateMachine + useWalletConnect
 *   3. Route vers le bon panel selon currentState
 *   4. Gère SUCCESS → appel onSuccess(userData) + fermeture
 *   5. Émet l'événement DOM `earni:signature_obtained` quand SIGNATURE_OBTAINED est
 *      dispatché (pour avancer le stepper dans WalletConnectingPanel)
 *
 * Règles DA :
 *   §14   — Zéro import @mui/@material-ui
 *   §20   — Zéro littéral couleur hors theme.js
 *   §18 ter — Au moins 1 breakpoint responsive
 *   §12 undecies — Casse grammaticale sur tous les textes UI
 *   §12 sexies — Croix de fermeture cerclée en haut à droite du header violet
 *   §8.7  — Aucun import Redux (le parent gère via onSuccess)
 *
 * @param {boolean}    isOpen           — montage/démontage du modal
 * @param {function}   onClose          — callback de fermeture (parent)
 * @param {function}   onSuccess(user)  — callback succès (parent dispatch Redux)
 * @param {string}     apiBaseUrl       — URL de base API (process.env.REACT_APP_SERVER)
 * @param {number}     chainId          — 43114 prod / 43113 Fuji staging
 * @param {string[]}   supportedWallets — ['metamask','coinbase','trustwallet','binance']
 * @param {string}     [theme]          — 'GREY' (défaut) | 'LIGHT'
 */

import React, { useEffect, useRef, useCallback, useState } from 'react';
import { writeStoredUser } from '../../utils/auth/userStorage';
import { ModalShell }     from '../ui/ModalShell';
import { ModalHeader, ModalHeaderTitle, ModalCloseBtn } from '../ui/ModalDA';

import { useWalletStateMachine } from './hooks/useWalletStateMachine';
import { useWalletConnect }      from './hooks/useWalletConnect';

/* Panels */
import WalletPickerPanel       from './panels/WalletPickerPanel';
import EmailAuthPanel          from './panels/EmailAuthPanel';
import EmailVerifyPanel        from './panels/EmailVerifyPanel';
import ForgotPasswordPanel     from './panels/ForgotPasswordPanel';
import WalletConnectingPanel   from './panels/WalletConnectingPanel';
import WalletRegisterPanel     from './panels/WalletRegisterPanel';
import { MultiWalletSwitcher } from './panels/MultiWalletSwitcher';
import PrivateKeyReveal        from './panels/PrivateKeyReveal';

/* Hook email auth */
import { useEmailAuth }        from './hooks/useEmailAuth';

/* Hook export clé privée */
import { usePrivateKeyExport } from './hooks/usePrivateKeyExport';

import { addBreadcrumbWalletConnect } from '../../utils/functions/sentryUtils';

/* Styles — WalletModalCard disponible pour usage futur si on bypasse ModalShell */
// import { WalletModalCard } from './styles/EarniWalletModal.styles';

/* ─── Titres de panel par état ──────────────────────────────────────────────── */
const PANEL_TITLES = {
  IDLE:              'Get started',
  PICKING:           'Get started',
  EMAIL_LOGIN:       'Sign in',
  EMAIL_REGISTER:    'Create account',
  EMAIL_VERIFY:      'Verify your email',
  FORGOT_PASSWORD:   'Reset your password',
  WALLET_CONNECTING: 'Connecting wallet',
  WALLET_REGISTERING:'Create account',
  MANAGE:            'My wallet',
  KEY_OTP_REQUEST:   'Export recovery keys',
  KEY_OTP_VERIFY:    'Export recovery keys',
  KEY_DISPLAY:       'Export recovery keys',
  SUCCESS:           'Connected',
  ERROR:             'Connection error',
};

/* ─── Composant ─────────────────────────────────────────────────────────────── */
function EarniWalletModal({
  isOpen,
  onClose,
  onSuccess,
  apiBaseUrl,
  chainId,
  supportedWallets = ['metamask', 'coinbase', 'trustwallet', 'binance'],
  theme = 'GREY',
  /**
   * initialFlow — flow d'ouverture initial.
   *   'connect' (défaut) → PICKING (flux connexion/inscription standard)
   *   'manage'           → MANAGE (gestion des wallets liés, mode authentifié)
   */
  initialFlow = 'connect',
  /* Bloc collectibles dans le panel MANAGE (fournis par le parent §8.7 — pas de Redux ici).
     collectiblesCount=null → bloc masqué. */
  collectiblesCount = null,
  onViewCollectibles,
  /* previewState — PREVIEW UIX UNIQUEMENT : objet {currentState, ...contexte} pour forcer
     l'affichage d'un panel donné dans le système de snapshot (§39). Jamais passé en prod. */
  previewState = null,
  /* contextBanner — bannière contextuelle affichée en haut du panel PICKING (ex. ouverture
     depuis le toggle dark mode). Forme : { icon, message }. null → aucune bannière. */
  contextBanner = null,
}) {
  /* Machine d'état */
  const { state: smState, dispatch } = useWalletStateMachine();
  const { currentState, userData, keyMaterial, connectedAddress, registrationToken, pendingUser, linkMode, resetLinkSent } = smState;

  /* Référence pour détecter les transitions de SIGNATURE_OBTAINED */
  const prevStateRef = useRef(currentState);

  /* Hook connexion wallet (SIWE) */
  const { connectWallet, connectTestWallet, cancelConnect, isConnecting } = useWalletConnect({
    dispatch,
    apiBaseUrl,
    chainId,
  });

  /* Hook auth email */
  const emailAuth = useEmailAuth({ dispatch, apiBaseUrl });

  /* Hook export clé privée */
  const privateKeyExport = usePrivateKeyExport({
    dispatch,
    apiBaseUrl,
    keyMaterial,
    currentState,
  });

  /* Wallets liés (pour MANAGE et PrivateKeyReveal) */
  const [linkedWallets, setLinkedWallets] = useState([]);

  /* Rechargement de la liste des wallets liés depuis GET /auth/wallets */
  const refreshLinkedWallets = useCallback(async () => {
    if (!apiBaseUrl) return;
    try {
      const { getLinkedWallets } = await import('./services/WalletAuthService');
      const data = await getLinkedWallets(apiBaseUrl);
      setLinkedWallets(data?.wallets ?? []);
    } catch {
      /* Échec silencieux — la liste reste inchangée */
    }
  }, [apiBaseUrl]);

  /* Fonction pour ouvrir la modal avec un flow différent (passée à MultiWalletSwitcher) */
  const openModal = useCallback((flow) => {
    /* Pour le cas 'link' : passer en mode liaison (MANAGE → PICKING linkMode).
       Le connect appellera POST /auth/wallet/link au lieu de verify-signature. */
    if (flow === 'link') {
      dispatch({ type: 'OPEN_LINK' });
    }
  }, [dispatch]);

  /* ── Effet : ouverture du modal → entrer dans PICKING ou MANAGE ──────────────
     Sans ça, le modal s'ouvre mais reste à IDLE (renderPanel ne rend rien) → corps vide.
     OPEN       : IDLE → PICKING (flux connect)
     OPEN_MANAGE: IDLE → MANAGE  (flux manage — wallets liés mode authentifié)
     RESET au close est géré par handleClose. */
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!isOpen) return;
    /* PREVIEW UIX : hydrate un état forcé une seule fois (garde anti-boucle). */
    if (previewState && !hydratedRef.current) {
      hydratedRef.current = true;
      dispatch({ type: '__PREVIEW_HYDRATE__', payload: previewState });
      return;
    }
    if (!previewState && currentState === 'IDLE') {
      if (initialFlow === 'manage') {
        dispatch({ type: 'OPEN_MANAGE' });
      } else {
        dispatch({ type: 'OPEN' });
      }
    }
  }, [isOpen, currentState, dispatch, initialFlow, previewState]);

  /* ── Effet : émettre événement DOM pour WalletConnectingPanel stepper step 3 ── */
  useEffect(() => {
    /*
     * useWalletStateMachine retourne state immuable après SIGNATURE_OBTAINED.
     * Le reducer ne change pas currentState mais le composant doit
     * avancer le stepper. On détecte le dispatch côté effet en écoutant
     * l'action elle-même via un wrapper de dispatch.
     */
    prevStateRef.current = currentState;
  }, [currentState]);

  /* ── Effet : SUCCESS → onSuccess + fermeture ──────────────────────────────── */
  useEffect(() => {
    if (currentState === 'SUCCESS' && userData) {
      /* Stocker en localStorage (comportement existant préservé) */
      try {
        writeStoredUser(userData);
        /* Horodatage d'authentification pour la fenêtre de reconnexion 5 min (useWalletConnect). */
        localStorage.setItem('_authTs', String(Date.now()));
        if (!userData.epicUserId && !userData.epicUsername) {
          localStorage.setItem('_first', String(true));
        }
      } catch { /* silencieux */ }

      addBreadcrumbWalletConnect();
      /* Remonter au parent pour dispatch Redux */
      if (typeof onSuccess === 'function') {
        onSuccess(userData);
      }

      /* Fermer le modal après un court délai (laisse voir le panel SUCCESS si présent) */
      const t = setTimeout(() => {
        handleClose();
      }, 800);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentState, userData]);

  /* ── Fermeture ─────────────────────────────────────────────────────────────── */
  const handleClose = useCallback(() => {
    dispatch({ type: 'RESET' });
    if (typeof onClose === 'function') onClose();
  }, [dispatch, onClose]);

  /* ── Dispatch wrappé : intercepte SIGNATURE_OBTAINED pour l'événement DOM ─── */
  const wrappedDispatch = useCallback((action) => {
    dispatch(action);
    if (action.type === 'SIGNATURE_OBTAINED') {
      /* Notifier WalletConnectingPanel pour avancer le stepper à l'étape 3 */
      try {
        window.dispatchEvent(new CustomEvent('earni:signature_obtained'));
      } catch { /* silencieux */ }
    }
  }, [dispatch]);

  /* ── Titre courant ─────────────────────────────────────────────────────────── */
  /* En linkMode, le picker et la connexion concernent la liaison d'un wallet
     au compte courant → titre dédié plutôt que "Sign in"/"Connecting wallet". */
  const title = (linkMode && (currentState === 'PICKING' || currentState === 'WALLET_CONNECTING'))
    ? 'Link a wallet'
    : (PANEL_TITLES[currentState] || 'Sign in');

  /* ── Rendu du bon panel selon currentState ─────────────────────────────────── */
  function renderPanel() {
    switch (currentState) {

      case 'PICKING':
        return (
          <WalletPickerPanel
            dispatch={wrappedDispatch}
            connectWallet={connectWallet}
            connectTestWallet={connectTestWallet}
            isConnecting={isConnecting}
            supportedWallets={supportedWallets}
            linkMode={linkMode}
            contextBanner={contextBanner}
          />
        );

      case 'EMAIL_LOGIN':
      case 'EMAIL_REGISTER':
        return (
          <EmailAuthPanel
            currentState={currentState}
            dispatch={wrappedDispatch}
            emailAuth={emailAuth}
          />
        );

      /* Étape de vérification email 2FA post-inscription EarniConnect */
      case 'EMAIL_VERIFY':
        return (
          <EmailVerifyPanel
            dispatch={wrappedDispatch}
            apiBaseUrl={apiBaseUrl}
            pendingUser={pendingUser}
          />
        );

      /* Flow mot de passe oublié (depuis EMAIL_LOGIN) */
      case 'FORGOT_PASSWORD':
        return (
          <ForgotPasswordPanel
            dispatch={wrappedDispatch}
            resetLinkSent={resetLinkSent}
          />
        );

      /* Gestion des wallets liés (mode authentifié, initialFlow='manage') */
      case 'MANAGE':
        return (
          <MultiWalletSwitcher
            dispatch={wrappedDispatch}
            apiBaseUrl={apiBaseUrl}
            linkedWallets={linkedWallets}
            refreshLinkedWallets={refreshLinkedWallets}
            openModal={openModal}
            collectiblesCount={collectiblesCount}
            onViewCollectibles={onViewCollectibles}
          />
        );

      case 'WALLET_CONNECTING':
      case 'ERROR':
        return (
          <WalletConnectingPanel
            stateMachineState={smState}
            dispatch={wrappedDispatch}
            cancelConnect={cancelConnect}
          />
        );

      case 'WALLET_REGISTERING':
        return (
          <WalletRegisterPanel
            dispatch={wrappedDispatch}
            apiBaseUrl={apiBaseUrl}
            connectedAddress={connectedAddress}
            registrationToken={registrationToken}
          />
        );

      /* Export de clé privée (wallets GENERATED) — états OTP + affichage */
      case 'KEY_OTP_REQUEST':
      case 'KEY_OTP_VERIFY':
      case 'KEY_DISPLAY':
        return (
          <PrivateKeyReveal
            dispatch={wrappedDispatch}
            apiBaseUrl={apiBaseUrl}
            keyMaterial={keyMaterial}
            currentState={currentState}
            linkedWallets={linkedWallets.length > 0 ? linkedWallets : (smState.userData?.linkedWallets ?? [])}
          />
        );

      /* État SUCCESS — fermeture pilotée par l'effet, pas de panel dédié */
      case 'SUCCESS':
      case 'IDLE':
      default:
        return null;
    }
  }

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={handleClose}
      $width="28rem"
      aria-label={title}
    >
      {/* Remplacement de ModalCard standard par WalletModalCard (override radius/bg selon theme) */}
      {/* Note : ModalShell fournit déjà ModalCard — on utilise son children slot */}

      {/* Header violet DA avec croix cerclée §12 sexies */}
      <ModalHeader>
        <ModalHeaderTitle>{title}</ModalHeaderTitle>
        <ModalCloseBtn
          onClick={handleClose}
          aria-label="Close"
          title="Close"
        >
          ✕
        </ModalCloseBtn>
      </ModalHeader>

      {/* Panel actif selon currentState */}
      {renderPanel()}
    </ModalShell>
  );
}

export default EarniWalletModal;
